import { useRef, useState, type Dispatch, type RefObject, type SetStateAction } from 'react';
import { Check, ImagePlus, Loader2, Plus, X } from 'lucide-react';
import { AdminGlassDisclosure } from '@/components/admin/AdminGlassDisclosure';
import { CFG_STEP_COPY } from '@/lib/configuratorStepCopy';
import { resolveConfiguratorTileHint } from '@/lib/configuratorTileHint';
import { PRYAMOY_CONFIG_PRESET, type ConfigStepKey } from '@/lib/configuratorSchema';
import { PROTOCOL_THEME } from '@/protocolTheme';
import type { AdminCatalogSubtypeRow, AdminCatalogSystem } from '@/api';
import {
  ALL_SUBTYPE_CONFIG_STEPS,
  SUBTYPE_SETUP_STEPS,
  type SubtypeOptionSchema,
} from './subtypeOptionSchema';

type SetupStepKey = (typeof SUBTYPE_SETUP_STEPS)[number]['key'];

type Props = {
  activeSubtype: AdminCatalogSubtypeRow;
  catalogWrite: boolean;
  skuTotal: number;
  schemaForm: SubtypeOptionSchema;
  setSchemaForm: Dispatch<SetStateAction<SubtypeOptionSchema>>;
  schemaSaving: boolean;
  onSave: () => void;
  onToggleActive: () => void;
  systemIdDraft: string;
  setSystemIdDraft: (v: string) => void;
  catalogSystems: AdminCatalogSystem[];
  subtypeImgRef: RefObject<HTMLInputElement>;
  subtypeImgUploading: boolean;
  onSubtypeImageSelected: (file: File) => void;
  formulaDraft: string;
  setFormulaDraft: (v: string) => void;
  formulaPreview: string | null;
  setFormulaPreview: (v: string | null) => void;
  newColorInput: string;
  setNewColorInput: (v: string) => void;
  catAssignInput: string;
  setCatAssignInput: (v: string) => void;
  assignByCatBusy: boolean;
  onAssignByCategory: () => void;
  batchBusy: boolean;
  onBatchProtocol: (proto: string) => void;
};

const ALL_PROTOCOLS = Object.keys(PROTOCOL_THEME);

export function SubtypeSetupPanel({
  activeSubtype,
  catalogWrite,
  skuTotal,
  schemaForm,
  setSchemaForm,
  schemaSaving,
  onSave,
  onToggleActive,
  systemIdDraft,
  setSystemIdDraft,
  catalogSystems,
  subtypeImgRef,
  subtypeImgUploading,
  onSubtypeImageSelected,
  formulaDraft,
  setFormulaDraft,
  formulaPreview,
  setFormulaPreview,
  newColorInput,
  setNewColorInput,
  catAssignInput,
  setCatAssignInput,
  assignByCatBusy,
  onAssignByCategory,
  batchBusy,
  onBatchProtocol,
}: Props) {
  const [activeStep, setActiveStep] = useState<SetupStepKey>('vitrine');
  const [newTagInput, setNewTagInput] = useState('');
  const [newProfileColorInput, setNewProfileColorInput] = useState('');
  const [newMotorColorInput, setNewMotorColorInput] = useState('');
  const sectionRefs = useRef<Partial<Record<SetupStepKey, HTMLDivElement | null>>>({});

  const tilePreview = resolveConfiguratorTileHint(
    { tile_hint: schemaForm.tile_hint, variants_count: skuTotal || undefined },
  );

  const enabledSteps = schemaForm.steps ?? [];
  const hasParams = enabledSteps.some((k) =>
    ['protocol', 'color', 'size', 'control', 'profile_color', 'motor_color'].includes(k),
  );

  const scrollToStep = (key: SetupStepKey) => {
    setActiveStep(key);
    sectionRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleStep = (key: ConfigStepKey) => {
    setSchemaForm((p) => {
      const steps = p.steps ?? [];
      return {
        ...p,
        steps: steps.includes(key) ? steps.filter((x) => x !== key) : [...steps, key],
      };
    });
  };

  const toggleProtocol = (pr: string) => {
    setSchemaForm((p) => {
      const ps = p.protocols ?? [];
      return {
        ...p,
        protocols: ps.includes(pr) ? ps.filter((x) => x !== pr) : [...ps, pr],
      };
    });
  };

  const addColor = () => {
    const c = newColorInput.trim();
    if (!c) return;
    setSchemaForm((p) => {
      const cs = p.colors ?? [];
      return cs.includes(c) ? p : { ...p, colors: [...cs, c] };
    });
    setNewColorInput('');
  };

  const patchStepCopy = (stepKey: ConfigStepKey, field: 'title' | 'hint', value: string) => {
    setSchemaForm((prev) => ({
      ...prev,
      step_copy: {
        ...(prev.step_copy ?? {}),
        [stepKey]: { ...(prev.step_copy?.[stepKey] ?? {}), [field]: value },
      },
    }));
  };

  const addListItem = (
    field: 'control_tags' | 'profile_colors' | 'motor_colors' | 'colors',
    value: string,
  ) => {
    const v = value.trim();
    if (!v) return;
    setSchemaForm((p) => {
      const list = p[field] ?? [];
      return list.includes(v) ? p : { ...p, [field]: [...list, v] };
    });
  };

  const removeListItem = (
    field: 'control_tags' | 'profile_colors' | 'motor_colors' | 'colors',
    value: string,
  ) => {
    setSchemaForm((p) => ({ ...p, [field]: (p[field] ?? []).filter((x) => x !== value) }));
  };

  const applyPryamoyPreset = () => {
    setSchemaForm({
      steps: [...(PRYAMOY_CONFIG_PRESET.steps ?? [])],
      protocols: [...(PRYAMOY_CONFIG_PRESET.protocols ?? [])],
      colors: [],
      control_tags: [...(PRYAMOY_CONFIG_PRESET.control_tags ?? [])],
      profile_colors: [...(PRYAMOY_CONFIG_PRESET.profile_colors ?? [])],
      motor_colors: [...(PRYAMOY_CONFIG_PRESET.motor_colors ?? [])],
      protocol_groups: (PRYAMOY_CONFIG_PRESET.protocol_groups ?? []).map((g) => ({
        ...g,
        options: [...g.options],
      })),
      dim_label: PRYAMOY_CONFIG_PRESET.dim_label ?? '',
      tile_hint: schemaForm.tile_hint ?? '',
      step_copy: schemaForm.step_copy,
      pricing: schemaForm.pricing,
      image_url: schemaForm.image_url,
    });
    setActiveStep('params');
    scrollToStep('params');
  };

  const openForStep = (key: SetupStepKey) => activeStep === key;

  return (
    <div className="space-y-3 pb-24">
      {/* Карта: что где */}
      <div
        className="rounded-xl px-3 py-2.5 text-[10px] leading-relaxed"
        style={{ background: 'rgba(99,179,237,0.08)', border: '1px solid rgba(99,179,237,0.22)', color: 'var(--text-2)' }}
      >
        <p className="font-bold mb-1" style={{ color: '#93D7FF' }}>Как устроено</p>
        <p>
          <strong style={{ color: 'var(--text-1)' }}>Подтип «{activeSubtype.title}»</strong> — плитка в каталоге
          («{tilePreview ?? 'подпись'}») и общие шаги калькулятора.
        </p>
        <p className="mt-1">
          <strong style={{ color: 'var(--text-1)' }}>Системы MR‑2234…</strong> — вкладки внутри конфигуратора:
          маркетинг, артикулы, цены (кнопка «→ таб в конфигураторе» в списке).
        </p>
      </div>

      {/* Step rail */}
      <div
        className="flex gap-1.5 overflow-x-auto pb-1 -mx-0.5 px-0.5"
        role="tablist"
        aria-label="Шаги настройки подтипа"
      >
        {SUBTYPE_SETUP_STEPS.map((st, i) => {
          const on = activeStep === st.key;
          return (
            <button
              key={st.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => scrollToStep(st.key)}
              className="press flex-shrink-0 flex flex-col items-start min-w-[4.5rem] px-2.5 py-2 rounded-xl text-left transition-all"
              style={
                on
                  ? { background: 'rgba(99,179,237,0.18)', border: '1px solid rgba(99,179,237,0.45)', color: '#93D7FF' }
                  : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'var(--text-3)' }
              }
            >
              <span className="text-[9px] font-bold opacity-70">{i + 1}</span>
              <span className="text-[11px] font-bold leading-tight">{st.label}</span>
            </button>
          );
        })}
      </div>

      {/* 1 Витрина */}
      <div ref={(el) => { sectionRefs.current.vitrine = el; }}>
        <AdminGlassDisclosure
          accent="cyan"
          open={openForStep('vitrine')}
          onOpenChange={(o) => o && setActiveStep('vitrine')}
          title="1 · Витрина"
          subtitle="Видимость, фото, подпись на плитке"
          badge={
            <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)', color: 'var(--text-3)' }}>
              {activeSubtype.is_active ? 'на витрине' : 'скрыт'}
            </span>
          }
        >
          <div className="px-3 pb-3 space-y-4">
            <div className="flex flex-wrap gap-2 justify-end">
              {catalogWrite && (
                <button
                  type="button"
                  onClick={onToggleActive}
                  className="press text-[10px] px-2.5 py-1 rounded-full font-bold"
                  style={
                    activeSubtype.is_active
                      ? { background: 'rgba(72,187,120,0.15)', color: '#68D391', border: '1px solid rgba(72,187,120,0.3)' }
                      : { background: 'rgba(252,129,74,0.15)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.3)' }
                  }
                >
                  {activeSubtype.is_active ? '● На витрине' : '● Скрыт'}
                </button>
              )}
              <span
                className="text-[10px] px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(159,122,234,0.12)', color: '#B794F4', border: '1px solid rgba(159,122,234,0.25)' }}
              >
                {skuTotal} SKU
              </span>
            </div>

            {skuTotal === 0 && !schemaForm.pricing?.formula && (
              <p className="text-[10px] rounded-lg px-2.5 py-2" style={{ background: 'rgba(252,129,74,0.08)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.25)' }}>
                Нет SKU и нет формулы — клиент может не увидеть подтип. Шаг 5 или формула в шаге 4.
              </p>
            )}

            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--text-3)' }}>
                Фото плитки
              </p>
              <input
                ref={subtypeImgRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onSubtypeImageSelected(f);
                }}
              />
              {schemaForm.image_url && (
                <div className="w-full h-24 rounded-xl overflow-hidden mb-2" style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <img src={schemaForm.image_url} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              {catalogWrite && (
                <button
                  type="button"
                  disabled={subtypeImgUploading}
                  onClick={() => subtypeImgRef.current?.click()}
                  className="press w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold disabled:opacity-40"
                  style={{ background: 'rgba(159,122,234,0.1)', border: '1px dashed rgba(159,122,234,0.4)', color: '#B794F4' }}
                >
                  {subtypeImgUploading ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
                  {subtypeImgUploading ? 'Загрузка…' : schemaForm.image_url ? 'Заменить фото' : 'Загрузить фото'}
                </button>
              )}
            </div>

            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--text-3)' }}>
                Подпись под названием на плитке
              </span>
              <input
                className="input-glass w-full px-3 py-2.5 rounded-xl text-sm"
                disabled={!catalogWrite}
                placeholder="Пусто → автоматически «N вар.»"
                value={schemaForm.tile_hint ?? ''}
                onChange={(e) => setSchemaForm((p) => ({ ...p, tile_hint: e.target.value }))}
              />
              <div className="mt-3 flex justify-center">
                <div
                  className="min-w-[7rem] px-4 py-3 rounded-2xl text-center"
                  style={{ background: 'rgba(99,179,237,0.12)', border: '1px solid rgba(99,179,237,0.35)' }}
                >
                  <p className="text-xs font-bold" style={{ color: 'var(--text-1)' }}>{activeSubtype.title}</p>
                  <p className="text-[9px] mt-1" style={{ color: 'var(--text-3)' }}>
                    {tilePreview ?? '—'}
                  </p>
                  <p className="text-[8px] mt-1.5 opacity-60">превью для клиента</p>
                </div>
              </div>
            </label>
          </div>
        </AdminGlassDisclosure>
      </div>

      {/* 2 Калькулятор */}
      <div ref={(el) => { sectionRefs.current.flow = el; }}>
        <AdminGlassDisclosure
          accent="violet"
          open={openForStep('flow')}
          onOpenChange={(o) => o && setActiveStep('flow')}
          title="2 · Калькулятор"
          subtitle="Какие блоки видит клиент и как они подписаны"
        >
          <div className="px-3 pb-3 space-y-4">
            <div>
              <p className="text-[10px] mb-2" style={{ color: 'var(--text-3)' }}>
                Включите только нужные шаги. Пустой список = все шаги по умолчанию.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {ALL_SUBTYPE_CONFIG_STEPS.map(({ key, label, icon, hint }) => {
                  const on = enabledSteps.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      title={hint}
                      disabled={!catalogWrite}
                      onClick={() => toggleStep(key)}
                      className="press flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] font-semibold disabled:opacity-40"
                      style={
                        on
                          ? { background: 'rgba(72,187,120,0.2)', color: '#68D391', border: '1px solid rgba(72,187,120,0.4)' }
                          : { background: 'rgba(255,255,255,0.05)', color: 'var(--text-3)', border: '1px solid rgba(255,255,255,0.08)' }
                      }
                    >
                      <span>{icon}</span> {label} {on && <Check size={9} />}
                    </button>
                  );
                })}
              </div>
            </div>

            {catalogWrite && (
              <button
                type="button"
                className="press w-full py-2 rounded-xl text-[11px] font-bold"
                style={{ background: 'rgba(99,179,237,0.14)', color: '#93D7FF', border: '1px solid rgba(99,179,237,0.35)' }}
                onClick={applyPryamoyPreset}
              >
                Пресет «Прямой» — шаги карниза + группы протоколов
              </button>
            )}

            {enabledSteps.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--text-3)' }}>
                  Тексты блоков (заголовок и серое описание)
                </p>
                {enabledSteps.map((stepKey) => {
                  const def = CFG_STEP_COPY[stepKey];
                  const row = schemaForm.step_copy?.[stepKey] ?? {};
                  if (!def) return null;
                  return (
                    <div
                      key={stepKey}
                      className="rounded-lg p-2 space-y-1.5"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                    >
                      <p className="text-[10px] font-semibold" style={{ color: '#93D7FF' }}>
                        {def.icon} {def.title}
                      </p>
                      <input
                        className="input-glass w-full px-2 py-1.5 rounded-lg text-xs"
                        disabled={!catalogWrite}
                        placeholder={`Заголовок: ${def.title}`}
                        value={row.title ?? ''}
                        onChange={(e) => patchStepCopy(stepKey, 'title', e.target.value)}
                      />
                      <textarea
                        className="input-glass w-full px-2 py-1.5 rounded-lg text-xs min-h-[52px] resize-y"
                        disabled={!catalogWrite}
                        placeholder={def.hint}
                        value={row.hint ?? ''}
                        onChange={(e) => patchStepCopy(stepKey, 'hint', e.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </AdminGlassDisclosure>
      </div>

      {/* 3 Параметры */}
      <div ref={(el) => { sectionRefs.current.params = el; }}>
        <AdminGlassDisclosure
          accent="amber"
          open={openForStep('params')}
          onOpenChange={(o) => o && setActiveStep('params')}
          title="3 · Параметры шагов"
          subtitle="Ограничения для протоколов, цветов, размера"
          badge={!hasParams && enabledSteps.length > 0 ? <span className="text-[9px] text-white/40">не нужно</span> : undefined}
        >
          <div className="px-3 pb-3 space-y-4">
            {!hasParams && (
              <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>
                Включите шаг «Протокол», «Цвет» или «Размер» в шаге 2 — здесь появятся настройки.
              </p>
            )}

            {enabledSteps.includes('protocol') && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--text-3)' }}>
                  Протоколы в шаге
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {ALL_PROTOCOLS.map((pr) => {
                    const th = PROTOCOL_THEME[pr];
                    const on = (schemaForm.protocols ?? []).includes(pr);
                    return (
                      <button
                        key={pr}
                        type="button"
                        disabled={!catalogWrite}
                        onClick={() => toggleProtocol(pr)}
                        className="press flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[10px] font-semibold disabled:opacity-40"
                        style={
                          on
                            ? { background: th.bg, color: th.text, border: `1px solid ${th.border}` }
                            : { background: 'rgba(255,255,255,0.05)', color: 'var(--text-3)', border: '1px solid rgba(255,255,255,0.1)' }
                        }
                      >
                        {th.icon} {th.label} {on && <Check size={9} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {enabledSteps.includes('control') && (
              <div className="space-y-2">
                <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--text-3)' }}>
                  Управление — теги (AND)
                </p>
                <div className="flex flex-wrap gap-1.5 mb-1">
                  {(schemaForm.control_tags ?? []).map((t) => (
                    <span
                      key={t}
                      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px]"
                      style={{ background: 'rgba(99,179,237,0.12)', color: '#93D7FF', border: '1px solid rgba(99,179,237,0.3)' }}
                    >
                      {t}
                      {catalogWrite && (
                        <button type="button" onClick={() => removeListItem('control_tags', t)} className="press">
                          <X size={9} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
                {catalogWrite && (
                  <div className="flex gap-2">
                    <input
                      className="input-glass flex-1 px-3 py-1.5 rounded-xl text-sm"
                      placeholder="Пульт ДУ, Фаза…"
                      value={newTagInput}
                      onChange={(e) => setNewTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          addListItem('control_tags', newTagInput);
                          setNewTagInput('');
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="press px-3 py-1.5 rounded-xl font-bold"
                      onClick={() => {
                        addListItem('control_tags', newTagInput);
                        setNewTagInput('');
                      }}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                )}
                {(schemaForm.protocol_groups ?? []).length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[9px]" style={{ color: 'var(--text-3)' }}>Группы протоколов</p>
                    {(schemaForm.protocol_groups ?? []).map((g, gi) => (
                      <p
                        key={g.id || gi}
                        className="text-[9px] px-2 py-1 rounded-lg"
                        style={{ background: 'rgba(255,255,255,0.04)', color: 'var(--text-2)' }}
                      >
                        {g.label ?? g.id} · {g.exclusive ? 'один из' : 'несколько'} · {g.options.join(', ')}
                      </p>
                    ))}
                  </div>
                )}
                {catalogWrite && !(schemaForm.protocol_groups ?? []).length && (
                  <button
                    type="button"
                    className="press text-[10px] underline"
                    style={{ color: 'var(--cyan)' }}
                    onClick={() =>
                      setSchemaForm((p) => ({
                        ...p,
                        protocol_groups: (PRYAMOY_CONFIG_PRESET.protocol_groups ?? []).map((g) => ({
                          ...g,
                          options: [...g.options],
                        })),
                      }))
                    }
                  >
                    Вставить группы Wi‑Fi / ZigBee / Matter + фазы
                  </button>
                )}
              </div>
            )}

            {enabledSteps.includes('profile_color') && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--text-3)' }}>
                  Цвета профиля
                </p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(schemaForm.profile_colors ?? []).map((c) => (
                    <span
                      key={c}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                      style={{ background: 'rgba(246,173,85,0.15)', color: '#F6AD55', border: '1px solid rgba(246,173,85,0.35)' }}
                    >
                      {c}
                      {catalogWrite && (
                        <button type="button" onClick={() => removeListItem('profile_colors', c)} className="press">
                          <X size={9} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
                {catalogWrite && (
                  <div className="flex gap-2">
                    <input
                      className="input-glass flex-1 px-3 py-1.5 rounded-xl text-sm"
                      placeholder="Белый, Чёрный…"
                      value={newProfileColorInput}
                      onChange={(e) => setNewProfileColorInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          addListItem('profile_colors', newProfileColorInput);
                          setNewProfileColorInput('');
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        addListItem('profile_colors', newProfileColorInput);
                        setNewProfileColorInput('');
                      }}
                      className="press px-3 py-1.5 rounded-xl font-bold"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {enabledSteps.includes('motor_color') && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--text-3)' }}>
                  Цвета мотора
                </p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(schemaForm.motor_colors ?? []).map((c) => (
                    <span
                      key={c}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                      style={{ background: 'rgba(160,174,192,0.15)', color: 'var(--text-2)' }}
                    >
                      {c}
                      {catalogWrite && (
                        <button type="button" onClick={() => removeListItem('motor_colors', c)} className="press">
                          <X size={9} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
                {catalogWrite && (
                  <div className="flex gap-2">
                    <input
                      className="input-glass flex-1 px-3 py-1.5 rounded-xl text-sm"
                      value={newMotorColorInput}
                      onChange={(e) => setNewMotorColorInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          addListItem('motor_colors', newMotorColorInput);
                          setNewMotorColorInput('');
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        addListItem('motor_colors', newMotorColorInput);
                        setNewMotorColorInput('');
                      }}
                      className="press px-3 py-1.5 rounded-xl font-bold"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {enabledSteps.includes('color') && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--text-3)' }}>
                  Цвета (legacy)
                </p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(schemaForm.colors ?? []).map((c) => (
                    <span
                      key={c}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                      style={{ background: 'rgba(246,173,85,0.15)', color: '#F6AD55', border: '1px solid rgba(246,173,85,0.35)' }}
                    >
                      {c}
                      {catalogWrite && (
                        <button
                          type="button"
                          onClick={() =>
                            setSchemaForm((p) => ({ ...p, colors: (p.colors ?? []).filter((x) => x !== c) }))
                          }
                          className="press opacity-70"
                        >
                          <X size={9} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
                {catalogWrite && (
                  <div className="flex gap-2">
                    <input
                      className="input-glass flex-1 px-3 py-1.5 rounded-xl text-sm"
                      placeholder="Добавить цвет"
                      value={newColorInput}
                      onChange={(e) => setNewColorInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') addColor();
                      }}
                    />
                    <button
                      type="button"
                      disabled={!newColorInput.trim()}
                      onClick={addColor}
                      className="press px-3 py-1.5 rounded-xl font-bold disabled:opacity-40"
                      style={{ background: 'rgba(246,173,85,0.18)', color: '#F6AD55', border: '1px solid rgba(246,173,85,0.35)' }}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {enabledSteps.includes('size') && (
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--text-3)' }}>
                  Подпись поля размера
                </span>
                <input
                  className="input-glass w-full px-3 py-2 rounded-xl text-sm"
                  disabled={!catalogWrite}
                  placeholder="Ширина проёма, м"
                  value={schemaForm.dim_label ?? ''}
                  onChange={(e) => setSchemaForm((p) => ({ ...p, dim_label: e.target.value }))}
                />
              </label>
            )}
          </div>
        </AdminGlassDisclosure>
      </div>

      {/* 4 Цена */}
      <div ref={(el) => { sectionRefs.current.price = el; }}>
        <AdminGlassDisclosure
          accent="slate"
          open={openForStep('price')}
          onOpenChange={(o) => o && setActiveStep('price')}
          title="4 · Цена без SKU"
          subtitle="Опционально, если нет матрицы в артикулах"
          density="compact"
        >
          <div className="px-3 pb-3 space-y-2">
            <label className="block">
              <span className="text-[9px] uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-3)' }}>
                Базовая цена (₽)
              </span>
              <input
                type="number"
                min={0}
                step={100}
                disabled={!catalogWrite}
                className="input-glass w-full px-3 py-2 rounded-xl text-sm"
                placeholder="0"
                value={schemaForm.pricing?.base_price ?? ''}
                onChange={(e) => {
                  const v = e.target.value === '' ? undefined : Number(e.target.value);
                  setSchemaForm((p) => ({ ...p, pricing: { ...(p.pricing ?? {}), base_price: v } }));
                }}
              />
            </label>
            <label className="block">
              <span className="text-[9px] uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-3)' }}>
                Формула (base_price, length_m, width_m)
              </span>
              <input
                disabled={!catalogWrite}
                className="input-glass w-full px-3 py-2 rounded-xl text-sm font-mono"
                placeholder="base_price * length_m"
                value={formulaDraft || schemaForm.pricing?.formula || ''}
                onChange={(e) => {
                  setFormulaDraft(e.target.value);
                  setFormulaPreview(null);
                }}
                onBlur={(e) => {
                  const f = e.target.value.trim();
                  setSchemaForm((p) => ({ ...p, pricing: { ...(p.pricing ?? {}), formula: f || undefined } }));
                  if (!f) {
                    setFormulaDraft('');
                    setFormulaPreview(null);
                    return;
                  }
                  try {
                    const bp = schemaForm.pricing?.base_price ?? 0;
                    const ctx: Record<string, number> = {
                      base_price: bp,
                      length_m: 2,
                      width_m: 1.5,
                      projection_m: 1,
                    };
                    const fn = new Function(...Object.keys(ctx), `return ${f}`);
                    const res = fn(...Object.values(ctx));
                    setFormulaPreview(`Превью (len=2): ${Math.round(Number(res)).toLocaleString('ru')} ₽`);
                  } catch {
                    setFormulaPreview('⚠ Ошибка в формуле');
                  }
                  setFormulaDraft('');
                }}
              />
            </label>
            {formulaPreview && (
              <p
                className="text-[10px] px-2 py-1 rounded-lg"
                style={{
                  background: formulaPreview.startsWith('⚠') ? 'rgba(252,129,74,0.1)' : 'rgba(72,187,120,0.1)',
                  color: formulaPreview.startsWith('⚠') ? '#FC814A' : '#68D391',
                }}
              >
                {formulaPreview}
              </p>
            )}
          </div>
        </AdminGlassDisclosure>
      </div>

      {/* 5 Данные */}
      <div ref={(el) => { sectionRefs.current.data = el; }}>
        <AdminGlassDisclosure
          accent="emerald"
          open={openForStep('data')}
          onOpenChange={(o) => o && setActiveStep('data')}
          title="5 · Данные и привязки"
          subtitle="Карточка конфигуратора, SKU, протоколы"
        >
          <div className="px-3 pb-3 space-y-4">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--text-3)' }}>
                Карточка конфигуратора (система по умолчанию)
              </span>
              <select
                className="input-glass w-full px-3 py-2.5 rounded-xl text-sm"
                disabled={!catalogWrite}
                value={systemIdDraft}
                onChange={(e) => setSystemIdDraft(e.target.value)}
                style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-1)', border: '1px solid var(--border-subtle)' }}
              >
                <option value="">— Не привязана —</option>
                {catalogSystems.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.title} (#{s.id})
                  </option>
                ))}
              </select>
              <p className="text-[9px] mt-1" style={{ color: 'var(--text-3)' }}>
                Список систем MR‑2234 настраивается в основном экране ветки.
              </p>
            </label>

            {catalogWrite && skuTotal === 0 && (
              <div
                className="rounded-xl p-3 space-y-2"
                style={{ background: 'rgba(99,179,237,0.05)', border: '1px solid rgba(99,179,237,0.2)' }}
              >
                <p className="text-[10px] font-bold" style={{ color: '#63B3ED' }}>
                  Привязать SKU по категории
                </p>
                <div className="flex gap-2">
                  <input
                    className="input-glass flex-1 px-3 py-2 rounded-xl text-xs"
                    placeholder="Категория (category)"
                    value={catAssignInput}
                    onChange={(e) => setCatAssignInput(e.target.value)}
                  />
                  <button
                    type="button"
                    disabled={assignByCatBusy || !catAssignInput.trim()}
                    onClick={onAssignByCategory}
                    className="press px-3 py-2 rounded-xl font-bold text-xs disabled:opacity-40"
                    style={{ background: 'rgba(99,179,237,0.2)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.4)' }}
                  >
                    {assignByCatBusy ? <Loader2 size={12} className="animate-spin" /> : 'Привязать'}
                  </button>
                </div>
              </div>
            )}

            {catalogWrite && skuTotal > 0 && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--text-3)' }}>
                  Протокол всем {skuTotal} SKU
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {ALL_PROTOCOLS.map((pr) => {
                    const th = PROTOCOL_THEME[pr];
                    return (
                      <button
                        key={pr}
                        type="button"
                        disabled={batchBusy}
                        onClick={() => onBatchProtocol(pr)}
                        className="press flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[10px] font-semibold disabled:opacity-40"
                        style={{ background: th.bg, color: th.text, border: `1px solid ${th.border}` }}
                      >
                        {batchBusy ? <Loader2 size={10} className="animate-spin" /> : th.icon} {th.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </AdminGlassDisclosure>
      </div>

      {/* Sticky save */}
      {catalogWrite && (
        <div
          className="fixed left-0 right-0 z-[43] px-3 pb-3 pointer-events-none"
          style={{ bottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            disabled={schemaSaving}
            onClick={onSave}
            className="press w-full py-3 rounded-xl text-sm font-bold pointer-events-auto disabled:opacity-50 shadow-lg"
            style={{ background: 'rgba(72,187,120,0.92)', color: '#0A192F', border: '1px solid rgba(104,211,145,0.6)' }}
          >
            {schemaSaving ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Сохраняем…
              </span>
            ) : (
              'Сохранить все шаги'
            )}
          </button>
        </div>
      )}
    </div>
  );
}
